import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { CheckCircle, Loader2, XCircle, CreditCard, ShieldCheck, Phone, CalendarDays, Hotel, Plane, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;
const fmt = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');

const Row = ({ label, value, bold, accent }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`${bold ? 'font-black text-lg' : 'font-semibold'} ${accent ? 'text-[#FF6600]' : 'text-slate-900'}`}>{value}</span>
  </div>
);

const Summary = ({ s }) => (
  <Card data-testid="pay-balance-package"><CardContent className="p-5 space-y-3 text-sm">
    <div className="font-bold text-slate-900 text-base">{s.category}</div>
    <div className="flex items-start gap-2 text-slate-700"><CalendarDays className="h-4 w-4 mt-0.5 text-[#FF6600]" />{fmt(s.flight.departure_date)} → {fmt(s.flight.return_date)}</div>
    <div className="flex items-start gap-2 text-slate-700"><Plane className="h-4 w-4 mt-0.5 text-[#FF6600]" />{s.flight.airline} · {s.flight.from} → {s.flight.to} · {s.flight.return_from} → {s.flight.from}</div>
    {s.hotels.map((h) => <div key={h.city} className="flex items-start gap-2 text-slate-700"><Hotel className="h-4 w-4 mt-0.5 text-[#FF6600]" />{h.city} — {h.name} {'★'.repeat(h.stars)} · {h.nights} night{h.nights !== 1 ? 's' : ''}</div>)}
    <div className="flex items-start gap-2 text-slate-700"><Users className="h-4 w-4 mt-0.5 text-[#FF6600]" />{s.passengers?.total} traveller{s.passengers?.total !== 1 ? 's' : ''} · {s.room_summary} · Lead: {s.lead_passenger}</div>
  </CardContent></Card>
);

export default function PayBalance({ bookingId, token, onGoHome }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/umrah/balance/${encodeURIComponent(bookingId)}?t=${encodeURIComponent(token || '')}`);
        const j = await res.json();
        if (!res.ok) throw new Error(j.detail || 'Unable to load this booking');
        setData(j);
      } catch (e) { setError(e.message); }
    })();
  }, [bookingId, token]);

  const pay = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${API_URL}/api/umrah/balance/${encodeURIComponent(bookingId)}/pay`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, origin_url: window.location.origin }) });
      const j = await res.json();
      if (!res.ok || !j.checkout_url) throw new Error(j.detail || 'Unable to start payment');
      window.location.href = j.checkout_url;
    } catch (e) { toast.error(e.message); setPaying(false); }
  };

  const paid = data?.paid_in_full;
  const overdue = data && !paid && data.balance_due_date && new Date(data.balance_due_date) < new Date(new Date().toDateString());
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="pay-balance-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="overflow-hidden border-0 shadow-2xl">
          <div className={`py-10 px-6 text-center text-white ${error ? 'bg-slate-700' : paid ? 'bg-emerald-600' : 'bg-[#FF6600]'}`}>
            {!data && !error && <Loader2 className="h-14 w-14 mx-auto mb-4 animate-spin" />}
            {error && <XCircle className="h-14 w-14 mx-auto mb-4" />}
            {paid && <CheckCircle className="h-14 w-14 mx-auto mb-4" />}
            {data && !paid && <CreditCard className="h-14 w-14 mx-auto mb-4" />}
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="pay-balance-title">{error ? 'Link not valid' : !data ? 'Loading your booking…' : paid ? 'Your package is paid in full' : 'Pay your remaining balance'}</h1>
            <p className="mt-2 text-white/85 text-sm sm:text-base max-w-lg mx-auto" data-testid="pay-balance-subtitle">{error || (data ? (paid ? 'Thank you — nothing more to pay. Your travel documents will follow by email.' : 'Complete your Umrah package securely online. No need to call.') : '')}</p>
          </div>
          {data && (
            <div className="bg-slate-900 p-6 text-center">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Booking reference</div>
              <div className="text-3xl sm:text-4xl font-black tracking-[0.15em] text-white select-all" data-testid="pay-balance-ref">{data.booking_id}</div>
            </div>
          )}
          {data && (
            <CardContent className="p-6" data-testid="pay-balance-summary">
              <Row label="Package total" value={gbp(data.total)} />
              <Row label="Paid so far" value={gbp(data.paid)} />
              <Row label={paid ? 'Balance' : 'Balance due'} value={gbp(data.balance_due)} bold accent={!paid} />
              {!paid && data.balance_due_date && <Row label="Due by" value={<span className={overdue ? 'text-red-600' : ''}>{fmt(data.balance_due_date)}{overdue ? ' · overdue' : ''}</span>} />}
              {paid && data.balance_paid_at && <Row label="Balance paid" value={fmt(data.balance_paid_at)} />}
              {data.can_pay && (
                <Button onClick={pay} disabled={paying} className="w-full h-14 mt-5 bg-[#FF6600] hover:bg-[#e65c00] text-white text-lg font-bold" data-testid="pay-balance-btn">
                  {paying ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CreditCard className="h-5 w-5 mr-2" />}{paying ? 'Redirecting to secure payment…' : `Pay ${gbp(data.balance_due)} now`}
                </Button>
              )}
              {!paid && !data.can_pay && <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="pay-balance-blocked">The balance can be paid once your deposit has been received. Please contact the Umrah Team if you need help.</div>}
              <p className="text-xs text-slate-500 mt-3 text-center">Receipt will be sent to {data.contact_email_masked}</p>
            </CardContent>
          )}
        </Card>
        {data && <Summary s={data} />}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Secured by Revolut · ATOL protected</div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#FF6600]" /> Umrah Team 01582 800 304</div>
        </div>
        <Button onClick={onGoHome} variant="outline" className="w-full h-12 font-semibold" data-testid="pay-balance-home-btn">Back to Flight380</Button>
      </div>
    </div>
  );
}
